//#region types
import { DialogEnums } from "models/enums";
import { MoreActionMenuItem } from "models/propTypes/menuPropTypes";
import { TranslationKeys } from "translations";
import { IconDefinitionConstants } from "utils/constants";
import { CostForwardingSource } from "models/utilTypes/costForwardingTypes";
import { getCostForwardingFormValues, getCostForwardingMenuEntry } from "./costForwardingMarkingModel";
import { CostForwardingMarkingResult } from "./useCostForwardingMarking";
//#endregion
//#region components
import { useCheckRelations } from "components/common";
import useInvoicePermissions from "components/invoice/useInvoicePemissions";
import { useCostForwardingService } from "serviceHooks";
import CostForwardingDialogForm from "./costForwardingDialogForm";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";

/**
 * The document page's more-actions entry: marking a document, changing or removing a ready marking, or
 * detaching a cost from the customer invoice it is on. Undefined for users who may not write, and when the
 * document can no longer be forwarded and its cost is not on an invoice.
 */
function useCostForwardingMenuAction(
  source: CostForwardingSource,
  sourceId: number | undefined,
  supplierName: string | undefined,
  costForwarding: Pick<CostForwardingMarkingResult, "marking" | "isAvailable"> & { isForwardable: boolean },
  onMarkingWritten: () => void
): MoreActionMenuItem | undefined {
  const { getIsReadonlyUser } = useCheckRelations();
  const { canUseInvoices } = useInvoicePermissions();
  const { releaseMarking } = useCostForwardingService();

  const { marking, isAvailable, isForwardable } = costForwarding;
  const entry = getCostForwardingMenuEntry(marking, isAvailable, isForwardable, {
    isReadonlyUser: getIsReadonlyUser(),
    canUseInvoices: !!canUseInvoices,
  });
  if (!entry || !sourceId) return undefined;

  if (entry === "release" && marking) {
    return {
      menuKey: "costForwarding",
      description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.CostForwarding.Release),
      icon: IconDefinitionConstants.WCostForward,
      actionDialog: {
        isOpen: false,
        id: "releaseCostForwarding",
        title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.CostForwarding.ReleaseTitle),
        titleIcon: IconDefinitionConstants.WCostForward,
        dialogType: DialogEnums.DialogType.Message,
        maxWidth: "sm",
        actions: [
          {
            text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.CostForwarding.ReleaseConfirm),
            successMessage: TranslationKeys.CostForwarding.Released,
            onClick: () => releaseMarking(marking.id),
          },
        ],
        children: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.CostForwarding.ReleaseDescription),
      },
      onActionSuccess: onMarkingWritten,
    };
  }

  const title = LocalizationHelperInstance.getFormattedMessage(
    entry === "mark" ? TranslationKeys.CostForwarding.Mark : TranslationKeys.CostForwarding.Edit
  );

  return {
    menuKey: "costForwarding",
    description: title,
    icon: IconDefinitionConstants.WCostForward,
    actionDialog: {
      isOpen: false,
      id: "costForwarding",
      title,
      subTitle: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.CostForwarding.HelpText),
      titleIcon: IconDefinitionConstants.WCostForward,
      dialogType: DialogEnums.DialogType.Content,
      maxWidth: "sm",
      actions: [{ text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Save) }],
      children: (
        <CostForwardingDialogForm
          source={source}
          sourceId={sourceId}
          defaultValues={getCostForwardingFormValues(marking, true, supplierName)}
        />
      ),
    },
    onActionSuccess: onMarkingWritten,
  };
}

export default useCostForwardingMenuAction;
